import {
  registrationIdIsStateWasteCode,
  registrationStateOmitsDot,
  registrationStateOmitsForm,
} from "./constants";

/**
 * The dimensions the state-registration matcher scores a candidate on. Shared
 * so the scorer and the dialog's scoring-details table can never disagree about
 * how much each dimension is worth.
 */
export interface RegistrationMatchWeights {
  /** Federal waste code alignment — the primary signal. */
  code: number;
  /** DOT/UN number, a secondary identity anchor. */
  dot: number;
  /** Waste-description (name) similarity. */
  description: number;
  /** Form code — lowest weight, and tolerated when absent. */
  form: number;
  /**
   * State waste code. Active only for states where the registration's own id is
   * a state waste code; inactive (0) everywhere else.
   */
  stateCode: number;
}

/** The four base dimensions every state scores on. They sum to 1. */
const BASE_WEIGHTS: RegistrationMatchWeights = {
  code: 0.6,
  dot: 0.2,
  description: 0.15,
  form: 0.05,
  stateCode: 0,
};

/**
 * The weight the state waste code takes in states where it is the
 * registration's own id — the single strongest identity signal available, so it
 * takes half the total.
 */
const STATE_CODE_WEIGHT = 0.5;

/**
 * The dimension weights to use for a state. Every returned set sums to 1, so a
 * score is directly comparable across states.
 */
export function registrationMatchWeights(
  state: string | null | undefined
): RegistrationMatchWeights {
  // Zero out any dimension the state's registry never publishes, then
  // renormalize the survivors so they keep their relative proportions while
  // still summing to 1. A state that omits nothing renormalizes to itself.
  const zeroed: RegistrationMatchWeights = {
    ...BASE_WEIGHTS,
    dot: registrationStateOmitsDot(state) ? 0 : BASE_WEIGHTS.dot,
    form: registrationStateOmitsForm(state) ? 0 : BASE_WEIGHTS.form,
  };
  const kept = zeroed.code + zeroed.dot + zeroed.description + zeroed.form;
  const renormalized: RegistrationMatchWeights = {
    code: zeroed.code / kept,
    dot: zeroed.dot / kept,
    description: zeroed.description / kept,
    form: zeroed.form / kept,
    stateCode: 0,
  };

  if (!registrationIdIsStateWasteCode(state)) {
    return renormalized;
  }

  // The state waste code IS the registration's id, so it carries half the
  // weight. The renormalized dimensions keep their relative proportions
  // within the remaining half: they already sum to 1, so scaling them all by
  // 0.5 preserves their ratios.
  const scale = 1 - STATE_CODE_WEIGHT;
  return {
    stateCode: STATE_CODE_WEIGHT,
    code: renormalized.code * scale,
    dot: renormalized.dot * scale,
    description: renormalized.description * scale,
    form: renormalized.form * scale,
  };
}

/**
 * The same weights expressed as percentages, for display alongside a candidate's
 * per-dimension subscores. Derived from `registrationMatchWeights` rather than
 * restated, so the numbers a user sees are the ones the scorer actually used.
 * Not rounded — a state can carry fractional percentages (TX: 37.5 / 9.375 /
 * 3.125) and rounding here would stop the column summing to 100.
 */
export function registrationMatchWeightPercentages(
  state: string | null | undefined
): RegistrationMatchWeights {
  const weights = registrationMatchWeights(state);
  return {
    code: weights.code * 100,
    dot: weights.dot * 100,
    description: weights.description * 100,
    form: weights.form * 100,
    stateCode: weights.stateCode * 100,
  };
}
